import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Home } from 'lucide-react';
import type { ArticleSection } from '@/types/content';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { SafeSEO } from '@/components/seo/SafeSEO';
import { RelatedArticles } from '@/components/RelatedArticles';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { calculateReadingTime, generateMetaDescription, generateKeywords, extractPlainText } from '@/utils/seo';
import { useEffect, useMemo } from 'react';
import { getRandomGrantProfile, getRandomFrancisProfile } from '@/utils/profilePhotos';
import { SITE_URL } from '@/lib/seo';
import { getKeywordsForPath } from '@/data/seoKeywordMap';

import { getArticleByIdStatic, getArticleBySlugStatic, getRelatedArticlesByIds, type Article } from "@/lib/articles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Author bio data
const authorBios = {
  "Grant Callaghan": {
    name: "Grant Callaghan",
    bio: "Grant Callaghan is a parent, technology professional, and advocate for digital safety. As the founder of Joey, Grant combines his experience in technology with his passion for keeping children safe online. He regularly writes about parental controls, digital wellness, and the intersection of technology and family life.",
    image: getRandomGrantProfile()
  },
  "Francis Callaghan": {
    name: "Francis Callaghan",
    bio: "Francis Callaghan is a 12-year-old writer who provides a unique perspective on teen digital culture and social media trends. Through his articles, Francis helps bridge the gap between parents and children by explaining modern digital communication from a young person's point of view.",
    image: getRandomFrancisProfile()
  }
};

type ArticleResolution = {
  article: Article | null;
  redirectPath: string | null;
  notFound: boolean;
};

const resolveArticle = (id?: string, slug?: string): ArticleResolution => {
  if (typeof id === "string") {
    const numericId = Number(id);
    if (!Number.isNaN(numericId)) {
      const fromId = getArticleByIdStatic(numericId);
      if (fromId) {
        const canonicalPath = `/articles/${fromId.id}/${fromId.slug}`;
        const needsRedirect = slug !== fromId.slug;
        return {
          article: fromId,
          redirectPath: needsRedirect ? canonicalPath : null,
          notFound: false,
        };
      }
    }
  }

  if (typeof slug === "string") {
    const fromSlug = getArticleBySlugStatic(slug);
    if (fromSlug) {
      const canonicalPath = `/articles/${fromSlug.id}/${fromSlug.slug}`;
      const needsRedirect =
        !id || Number(id) !== fromSlug.id || slug !== fromSlug.slug;
      return {
        article: fromSlug,
        redirectPath: needsRedirect ? canonicalPath : null,
        notFound: false,
      };
    }
  }

  return { article: null, redirectPath: null, notFound: true };
};

const ArticleDetail = () => {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const navigate = useNavigate();

  const resolution = useMemo(() => resolveArticle(id, slug), [id, slug]);

  useEffect(() => {
    if (!resolution.redirectPath) {
      return;
    }

    navigate(resolution.redirectPath, { replace: true });
  }, [resolution.redirectPath, navigate]);

  useEffect(() => {
    if (!resolution.article) return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [resolution.article?.id]);

  if (resolution.notFound) {
    const canonicalUrl = `${SITE_URL}/articles/not-found`;
    return (
      <div className="min-h-screen bg-background">
        <SafeSEO title="Article Not Found" description="The article you're looking for doesn't exist." url={canonicalUrl} />
        <Navigation />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The article you're looking for doesn't exist or hasn't been published yet.
          </p>
          <Button asChild>
            <Link to="/articles">Back to Articles</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const article = resolution.article;

  const { cleanedContent, jsonLdBlocks } = useMemo(() => {
    if (!article) {
      return { cleanedContent: '', jsonLdBlocks: [] as string[] };
    }

    const blocks: string[] = [];
    const cleaned = article.content.replace(/```json\s*([\s\S]*?)```/gi, (match, inner) => {
      const trimmed = inner.trim();
      if (trimmed.startsWith('{') && trimmed.includes('"@context"')) {
        blocks.push(trimmed);
        return '';
      }
      return match;
    });

    return { cleanedContent: cleaned, jsonLdBlocks: blocks };
  }, [article]);

  // Calculate SEO data
  const fullContent = article.content;

  const readingTime = calculateReadingTime(fullContent);
  const metaDescription = article.metaDescription ?? generateMetaDescription(fullContent, 160);
  const keywords = generateKeywords(article.title, fullContent, article.tags);
  // Use slug directly from meta.json (same source as sitemap) - slug is guaranteed to exist
  const articleSlug = article.slug;
  const publishedDate = new Date(article.date).toISOString();
  const currentUrl = `${SITE_URL}/articles/${article.id}/${articleSlug}`;
  const OG_FALLBACK = `${SITE_URL}/img/og/joey-default-1200x630.png`;
  const ogImage = article?.image
    ? (article.image.startsWith('http') ? article.image : `${SITE_URL}${article.image}`)
    : OG_FALLBACK;
  const mappedKeywords = getKeywordsForPath(`/articles/${article.id}/${articleSlug}`);
  const combinedKeywords = Array.from(new Set([...mappedKeywords, ...keywords]));

  const authorBio = authorBios[article.author as keyof typeof authorBios];

  const renderContent = (section: ArticleSection) => {
    switch (section.type) {
      case 'heading':
        return (
          <h2 className="text-2xl font-semibold mt-8 mb-4 text-brand-navy">
            {section.content}
          </h2>
        );
      case 'paragraph':
        return (
          <p className="text-muted-foreground leading-relaxed mb-6">
            {section.content}
          </p>
        );
      case 'list':
        return (
          <ul className="list-disc list-inside space-y-2 mb-6 text-muted-foreground">
            {section.content.map((item: string, index: number) => (
              <li key={index} className="leading-relaxed">{item}</li>
            ))}
          </ul>
        );
      case 'quote':
        return (
          <blockquote className="border-l-4 border-primary pl-6 py-4 my-8 bg-primary/5 rounded-r-lg">
            <p className="text-lg italic text-brand-navy">{section.content}</p>
          </blockquote>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SafeSEO
        title={article.title}
        description={metaDescription}
        keywords={combinedKeywords}
        image={ogImage}
        url={currentUrl}
        ogType="article"
      />

      {/* OG tags are handled in SafeSEO via Helmet */}
      
      <Navigation />

      {/* Breadcrumb Navigation */}
      <section className="pt-24 pb-4 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="flex items-center gap-1">
                  <Home className="h-4 w-4" />
                  Home
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/articles">Articles</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{article.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </section>
      {/* Back Button */}
      <div className="container mx-auto px-4 pt-4">
        <button
          onClick={() => navigate('/articles')}
          className="group inline-flex items-center gap-3 rounded-full px-6 py-3 font-semibold text-white shadow-lg transition-transform duration-300 hover:shadow-xl hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary joey-gradient-button"
        >
          <ArrowLeft className="w-5 h-5 text-white transition-transform duration-300 group-hover:-translate-x-1" />
          <span>Back to Articles</span>
        </button>
      </div>

      <article className="container mx-auto px-4 pb-16">
        {/* Article Header */}
        <header className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Badge
              variant="outline"
              className={`font-medium text-white border-0 ${article.categories[0] === 'Online safety' ? 'bg-green-600' :
                  article.categories[0] === 'Online Safety' ? 'bg-green-600' :
                    article.categories[0] === 'Insights' ? 'bg-purple-600' :
                      article.categories[0] === 'Parental controls' ? 'bg-blue-600' :
                        'bg-joey-navy'
                }`}
            >
              {article.categories[0]}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{article.readTime} min read</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{article.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <time dateTime={publishedDate}>{article.date}</time>
            </div>
          </div>

          {/* Article Image */}
          {article.image && (
            <div className="relative rounded-xl overflow-hidden mb-12 shadow-lg">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[400px] object-cover"
              />
            </div>
          )}
        </header>

        {/* Article Content */}
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg max-w-none article-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
            >
              {cleanedContent || article.content}
            </ReactMarkdown>
          </div>

          {jsonLdBlocks.map((block, index) => (
            <script
              key={`article-jsonld-${index}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: block }}
            />
          ))}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
            {article.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="bg-muted/50">
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        {/* About the Author Section */}
        {authorBio && (
          <section className="max-w-4xl mx-auto mt-16">
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 border border-border/50">
              <h3 className="text-2xl font-semibold mb-6 text-brand-navy">About the Author</h3>

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <img
                    src={authorBio.image}
                    alt={authorBio.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-2 border-primary/20"
                  />
                </div>

                <div className="flex-1">
                  <h4 className="text-xl font-semibold mb-3 text-brand-navy">{authorBio.name}</h4>
                  <p className="text-muted-foreground leading-relaxed">{authorBio.bio}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Related Articles */}
                <RelatedArticles
          currentArticleId={article.id.toString()}
          articles={getRelatedArticlesByIds(article.relatedArticles || [])?.map((relatedArticle) => ({
            id: relatedArticle.id.toString(),
            slug: relatedArticle.slug,
            title: relatedArticle.title,
            excerpt: relatedArticle.excerpt || '',
            author: relatedArticle.author,
            date: relatedArticle.date,
            category: relatedArticle.categories[0],
            tags: relatedArticle.tags,
            readTime: relatedArticle.readTime,
            media: relatedArticle.image
              ? {
                  url: relatedArticle.image,
                  alt: relatedArticle.imageAlt || relatedArticle.title,
                }
              : undefined,
          }))}
        />
      </article>

      <Footer />
    </div>
  );
};

export default ArticleDetail;


